import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    CreditCard,
    CheckCircle2,
    AlertCircle,
    Clock,
    Search,
    FileText,
    ChevronDown,
    X,
} from "lucide-react";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { formatCurrency } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { AnimatedContainer } from "@/components/shared/AnimatedContainer";
import { BoletoTransaction, FinancialSummary } from "@/lib/api";
import { eachMonthOfInterval, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { Input } from "@/components/ui/input";
import { FinancialCompositionTable } from "./FinancialCompositionTable";
import { FinancialSkeleton } from "@/pages/CustomerPortal/Financial/component/FinancialSkeleton";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";

type FilterStatus = "all" | "overdue" | "upcoming" | "paid";

const EMPTY_MONTH_STATUS = "Sem fatura";
const toMonthSortKey = ( d: Date ) => d.getFullYear() * 100 + d.getMonth() + 1;

interface FinancialTableViewProps {
    transactions: BoletoTransaction[];
    summary: FinancialSummary;
    isLoading: boolean;
    onGenerateBoleto: ( transaction: BoletoTransaction ) => void;
    tableTitle?: string;
    isBackoffice?: boolean;
    date: DateRange | undefined;
    setDate: ( date: DateRange | undefined ) => void;
    currentGeneratingBoletoId?: number | null;
    unavailableBoletoIds?: ReadonlySet<number>;
}

export const FinancialTableView: React.FC<FinancialTableViewProps> = ( {
    transactions,
    summary,
    isLoading,
    onGenerateBoleto,
    tableTitle = "Boletos e Cobranças",
    isBackoffice = false,
    date,
    setDate,
    currentGeneratingBoletoId = null,
    unavailableBoletoIds,
} ) => {
    const [ activeFilter, setActiveFilter ] = useState<FilterStatus>( "all" );
    const [ searchQuery, setSearchQuery ] = useState( "" );
    const [ openMonths, setOpenMonths ] = useState<Set<string>>( new Set() );

    const groupedTransactions = useMemo( () => {
        if ( !transactions ) return [];

        let baseData = [ ...transactions ];

        // Filtro por Status baseado no statusLabel do backend
        if ( activeFilter === "overdue" ) {
            baseData = baseData.filter( item => item.statusLabel === "Vencido" );
        } else if ( activeFilter === "upcoming" ) {
            baseData = baseData.filter( item => item.statusLabel === "Em Aberto" );
        } else if ( activeFilter === "paid" ) {
            baseData = baseData.filter( item =>
                item.statusLabel === "Pago" ||
                ( Array.isArray( item.lines ) && item.lines.some( line => line.isDevolucao || line.typeLabel === 'Return' ) )
            );
        }

        // Filtro por Busca de Texto
        if ( searchQuery.trim() ) {
            const query = searchQuery.toLowerCase();
            baseData = baseData.filter( trans =>
                ( trans.monthYear && String( trans.monthYear ).toLowerCase().includes( query ) ) ||
                ( trans.dueDate && String( trans.dueDate ).toLowerCase().includes( query ) ) ||
                ( Array.isArray( trans.lines ) && trans.lines.some( line =>
                    ( line.invoiceId && String( line.invoiceId ).toLowerCase().includes( query ) ) ||
                    ( line.orderId && String( line.orderId ).toLowerCase().includes( query ) ) ||
                    ( line.salesOrderId && String( line.salesOrderId ).toLowerCase().includes( query ) ) ||
                    ( line.magentoOrderId && String( line.magentoOrderId ).toLowerCase().includes( query ) )
                ) )
            );
        }

        // Agrupamento por mês/ano
        const groups: Record<string, BoletoTransaction[]> = {};
        baseData.forEach( trans => {
            const key = trans.monthYear || "0/0";
            if ( !groups[ key ] ) groups[ key ] = [];
            groups[ key ].push( trans );
        } );

        // Converte para array (ordenado mais abaixo: Ano asc, depois Mês asc)
        const monthGroups = Object.entries( groups )
            .map( ( [ monthYear, items ] ) => {
                const parts = monthYear.split( '/' );
                let m = 0;
                let y = 0;

                if ( parts.length === 2 ) {
                    m = parseInt( parts[ 0 ], 10 ) || 0;
                    y = parseInt( parts[ 1 ], 10 ) || 0;
                } else {
                    const firstDate = items[ 0 ]?.dueDate ? new Date( items[ 0 ].dueDate ) : new Date();
                    m = firstDate.getMonth() + 1;
                    y = firstDate.getFullYear();
                }

                const sortKey = ( y * 100 ) + m;

                const sortedItems = [ ...items ].sort( ( a, b ) => {
                    return new Date( a.dueDate ).getTime() - new Date( b.dueDate ).getTime();
                } );

                const totalOld = items.reduce( ( sum, item ) => {
                    if ( Array.isArray( item.lines ) && item.lines.length > 0 ) {
                        const linesSum = item.lines
                            .filter( line => !line.isDevolucao && !line.isIsencao && line.typeLabel !== 'Return' && line.typeLabel !== 'Discount' )
                            .reduce( ( lineSum, line ) => lineSum + line.oldAmount, 0 );
                        return sum + linesSum;
                    }
                    return sum + item.oldAmount;
                }, 0 );

                const totalNew = items.reduce( ( sum, item ) => {
                    if ( Array.isArray( item.lines ) && item.lines.length > 0 ) {
                        const linesSum = item.lines
                            .filter( line => !line.isDevolucao && !line.isIsencao && line.typeLabel !== 'Return' && line.typeLabel !== 'Discount' )
                            .reduce( ( lineSum, line ) => lineSum + line.newAmount, 0 );
                        return sum + linesSum;
                    }
                    return sum + item.newAmount;
                }, 0 );

                let status = "Em Aberto";
                if ( items.some( item => item.statusLabel === "Vencido" ) ) status = "Vencido";
                else if ( items.every( item => item.statusLabel === "Pago" ) ) status = "Pago";

                return {
                    monthYear,
                    sortKey,
                    date: new Date( y, m - 1, 1 ),
                    items: sortedItems,
                    totalOld,
                    totalNew,
                    status,
                    hasCorrection: items.some( item => item.hasCorrection )
                };
            } );

        // Completa o período com os meses que não têm nenhuma fatura
        if ( date?.from && date?.to ) {
            const existingKeys = new Set( monthGroups.map( group => group.sortKey ) );

            eachMonthOfInterval( { start: date.from, end: date.to } ).forEach( monthDate => {
                const sortKey = toMonthSortKey( monthDate );
                if ( existingKeys.has( sortKey ) ) return;

                monthGroups.push( {
                    monthYear: `${monthDate.getMonth() + 1}/${monthDate.getFullYear()}`,
                    sortKey,
                    date: monthDate,
                    items: [],
                    totalOld: 0,
                    totalNew: 0,
                    status: EMPTY_MONTH_STATUS,
                    hasCorrection: false,
                } );
            } );
        }

        return monthGroups.sort( ( a, b ) => a.sortKey - b.sortKey ); // Ordena do mais antigo para o mais recente
    }, [ transactions, activeFilter, searchQuery, date ] );

    if ( isLoading ) {
        return <FinancialSkeleton />;
    }

    return (
        <div className="space-y-8">
            {/* Cards de Resumo Financeiro */}
            <div className={cn(
                "grid gap-4 md:grid-cols-2",
                isBackoffice ? "lg:grid-cols-4" : "lg:grid-cols-2"
            )}>
                {isBackoffice && (
                    <AnimatedContainer variant="scale" delay={0.05}>
                        <Card className="border-l-4 border-l-muted-foreground/50 shadow-sm hover:shadow-md transition-shadow">
                            <CardHeader className="pb-2 space-y-0">
                                <CardTitle className="text-sm font-bold text-muted-foreground flex justify-between items-center">
                                    Valor total
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                </CardTitle>
                                <CardDescription className="text-xs">Parcelas no período (com correções)</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-foreground">
                                    {formatCurrency( summary.total )}
                                </div>
                            </CardContent>
                        </Card>
                    </AnimatedContainer>
                )}

                <AnimatedContainer variant="scale" delay={0.1}>
                    <Card className="border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2 space-y-0">
                            <CardTitle className="text-sm font-bold text-blue-500 flex justify-between items-center">
                                Em Aberto
                                <CreditCard className="h-4 w-4 text-blue-500" />
                            </CardTitle>
                            <CardDescription className="text-xs">Total de parcelas no período selecionado</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-blue-600">
                                {formatCurrency( summary.emAberto )}
                            </div>
                        </CardContent>
                    </Card>
                </AnimatedContainer>

                {isBackoffice && (
                    <AnimatedContainer variant="scale" delay={0.15}>
                        <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow">
                            <CardHeader className="pb-2 space-y-0">
                                <CardTitle className="text-sm text-green-500 font-bold flex justify-between items-center">
                                    Liquidado
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                </CardTitle>
                                <CardDescription className="text-xs">Total liquidado no período selecionado</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-green-600">
                                    {formatCurrency( summary.liquidado )}
                                </div>
                            </CardContent>
                        </Card>
                    </AnimatedContainer>
                )}

                <AnimatedContainer variant="scale" delay={0.2}>
                    <Card className="border-l-4 border-l-red-500 shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2 space-y-0">
                            <CardTitle className="text-sm font-bold text-red-500 flex justify-between items-center">
                                Vencidos
                                <AlertCircle className="h-4 w-4 text-red-500" />
                            </CardTitle>
                            <CardDescription className="text-xs">Total vencido no período selecionado</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-red-600">
                                {formatCurrency( summary.vencidos )}
                            </div>
                        </CardContent>
                    </Card>
                </AnimatedContainer>
            </div>

            {/* Gestão de Parcelas */}
            <Card className="border-border shadow-lg">
                <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <CardTitle className="flex items-center gap-2 text-xl">
                                <FileText className="h-5 w-5 text-primary" />
                                {tableTitle}
                            </CardTitle>
                            <CardDescription>
                                Acompanhe faturas mensais e detalhamento de pedidos.
                            </CardDescription>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant={activeFilter === "all" ? "default" : "outline"}
                                size="sm"
                                onClick={() => setActiveFilter( "all" )}
                                className="h-8 text-xs font-bold"
                            >
                                Todos
                            </Button>
                            <Button
                                variant={activeFilter === "overdue" ? "destructive" : "outline"}
                                size="sm"
                                onClick={() => setActiveFilter( "overdue" )}
                                className="h-8 text-xs font-bold"
                            >
                                Vencidos
                            </Button>
                            <Button
                                variant={activeFilter === "upcoming" ? "secondary" : "outline"}
                                size="sm"
                                onClick={() => setActiveFilter( "upcoming" )}
                                className="h-8 text-xs font-bold"
                            >
                                A vencer
                            </Button>
                            <Button
                                variant={activeFilter === "paid" ? "outline" : "outline"}
                                size="sm"
                                onClick={() => setActiveFilter( "paid" )}
                                className={cn( "h-8 text-xs font-bold", activeFilter === "paid" && "bg-green-600 text-white hover:bg-green-700 hover:text-white" )}
                            >
                                Pagos
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col md:flex-row justify-end items-center mb-6 gap-3">
                        <div className="relative w-full md:w-[350px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por NF ou Pedido..."
                                value={searchQuery}
                                onChange={( e ) => setSearchQuery( e.target.value )}
                                className="pl-9 pr-9"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery( "" )}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <DateRangePicker
                            date={date}
                            setDate={setDate}
                            className="w-full md:w-[320px]"
                        />
                    </div>

                    <div className="border rounded-xl overflow-hidden bg-white">
                        <Table>
                            <TableHeader className="bg-transparent border-none">
                                <TableRow className="hover:bg-transparent border-none">
                                    <TableHead className="w-[250px] font-bold text-muted-foreground uppercase text-[10px]">Mês</TableHead>
                                    <TableHead className="font-bold text-muted-foreground uppercase text-[10px] text-center">Vencimento</TableHead>
                                    <TableHead className="font-bold text-muted-foreground uppercase text-[10px] text-center">Valor</TableHead>
                                    <TableHead className="font-bold text-muted-foreground uppercase text-[10px] text-center">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody className="space-y-4">
                                {groupedTransactions.length > 0 ? (
                                    groupedTransactions.map( ( group, index ) => {
                                        const monthId = group.monthYear;
                                        const isMonthOpen = openMonths.has( monthId );
                                        const toggleMonth = () => setOpenMonths( prev => {
                                            const newSet = new Set( prev );
                                            newSet.has( monthId ) ? newSet.delete( monthId ) : newSet.add( monthId );
                                            return newSet;
                                        } );

                                        const isVencido = group.status === "Vencido";
                                        const isPago = group.status === "Pago";
                                        const isSemFatura = group.items.length === 0;

                                        return (
                                            <React.Fragment key={monthId}>
                                                <TableRow
                                                    onClick={toggleMonth}
                                                    className={cn(
                                                        "cursor-pointer transition-all border rounded-xl overflow-hidden mb-4 bg-white hover:bg-slate-50",
                                                        isMonthOpen && "border-primary/20"
                                                    )}
                                                >
                                                    <TableCell className="py-6 font-medium">
                                                        <div className="flex items-center gap-4">
                                                            <div className={cn(
                                                                "p-2 rounded-lg transition-transform bg-slate-50 text-primary",
                                                                isMonthOpen && "rotate-180 bg-muted"
                                                            )}>
                                                                <ChevronDown className="h-4 w-4" />
                                                            </div>
                                                            <span className="text-lg font-black capitalize text-foreground">
                                                                {format( group.date, "MMMM 'de' yyyy", { locale: ptBR } )}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <div className="flex items-center justify-center gap-2 text-muted-foreground font-bold">
                                                            --
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <div className="flex flex-col items-center">
                                                            <span className={cn(
                                                                "text-lg font-black",
                                                                isSemFatura ? "text-muted-foreground" :
                                                                    isVencido ? "text-red-600" : "text-foreground"
                                                            )}>
                                                                {formatCurrency( group.totalNew > 0 ? group.totalNew : group.totalOld )}
                                                            </span>
                                                            {group.hasCorrection && (
                                                                <span className="text-[11px] text-muted-foreground line-through font-bold">
                                                                    {formatCurrency( group.totalOld )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <Badge className={cn(
                                                            "rounded-full px-6 py-1.5 text-[10px] font-black uppercase border-none",
                                                            isSemFatura ? "bg-muted text-muted-foreground" :
                                                                isPago ? "bg-green-100 text-green-700" :
                                                                    isVencido ? "bg-red-100 text-red-600" :
                                                                        "bg-blue-100 text-blue-600"
                                                        )}>
                                                            {group.status}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>

                                                {isMonthOpen && (
                                                    <TableRow className="bg-transparent hover:bg-transparent">
                                                        <TableCell colSpan={4} className="pt-6">
                                                            <div className="px-4 py-2">
                                                                {isSemFatura ? (
                                                                    <EmptyState
                                                                        title="Nenhuma fatura para este mês"
                                                                        message={`Não há faturas registradas em ${format( group.date, "MMMM 'de' yyyy", { locale: ptBR } )}.`}
                                                                        className="py-8"
                                                                    />
                                                                ) : (
                                                                    <FinancialCompositionTable
                                                                        transactions={group.items}
                                                                        isBackoffice={isBackoffice}
                                                                        searchQuery={searchQuery}
                                                                        onViewBoleto={onGenerateBoleto}
                                                                        currentGeneratingBoletoId={currentGeneratingBoletoId}
                                                                        unavailableBoletoIds={unavailableBoletoIds}
                                                                        activeFilter={activeFilter}
                                                                    />
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    } )
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <EmptyState title="Nenhuma fatura encontrada" message="Não há registros financeiros para este cliente no período selecionado." className="py-12" />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
